import { AddAddressButton } from "../AddAddressButton"
import { Address } from "../Address"
import { ContactInfo } from "../ContactInfo"
import { PersonalInfo } from "../PersonalInfo"
import { PersonHeader } from "../PersonHeader"
import { PersonPanel } from "../PersonPanel"
import marker from "../../../assets/fonts/Location-marker.svg"
import { FC } from "react"
import { FormikType } from "../../../types/VisitReports/types"
import { VISITS_ADDRESS } from "../../../constants/CreateHousehold"
import "./styles.scss"
import { useTranslation } from "../../../hooks/useTranslation"
import { setPhoneSlot, setPhoneExtension } from "../../../utils/phoneSlots"
import { extractAddressFields } from "../../../utils/placeComponents";

type Props = {
  formik: FormikType
  personIndex: number
  salutations: string[]
  addressesTypes: string[]
  isUpdate?: boolean
}

export const Person: FC<Props> = ({
  formik,
  personIndex,
  salutations,
  isUpdate,
}) => {
  const { p } = useTranslation('contact_info');
  const path = isUpdate ? "updated_persons" : "new_persons"

  // A contact keeps at most one optional WORK address (the home address is
  // household-owned, edited via "Edit Home details"). New addresses are Work.
  const addAddress = () => {
    formik.setFieldValue(`${path}[${personIndex}].addresses`, [
      ...(formik?.values?.[path]?.[personIndex]?.addresses || []),
      { ...VISITS_ADDRESS, type: "Work" },
    ])
  }

  const handleIsPrimaryPerson = (e: any, index: number) => {
    if (e.target.type === "radio") {
      formik?.values?.[path]?.forEach((_, ind) => {
        if (index === ind) {
          formik.setFieldValue(`${path}[${ind}].isMain`, true)
        } else {
          formik.setFieldValue(`${path}[${ind}].isMain`, false)
        }
      })
    }
  }

  const handleDeletePerson = () => {
    formik?.values?.[path]?.splice(personIndex, 1)
    formik.setFieldValue(`${path}`, [...(formik?.values?.[path] || [])])
  }

  const handleSalutation = (value: string) => {
    formik.setFieldValue(`${path}[${personIndex}].salutation`, value)
  }

  const setGender = (value: string) => {
    formik.setFieldValue(`${path}[${personIndex}].gender`, value)
  }

  const handleFirstName = (e: any) => {
    formik.setFieldValue(`${path}[${personIndex}].first_name`, e.target.value)
  }

  const handleLastName = (e: any) => {
    formik.setFieldValue(`${path}[${personIndex}].last_name`, e.target.value)
  }

  const setIsJewish = (e: any) => {
    formik.setFieldValue(
      `${path}[${personIndex}].is_jewish`,
      e.target.checked ? 1 : 0
    )
  }

  const setIsChild = (e: any) => {
    formik.setFieldValue(
      `${path}[${personIndex}].is_child`,
      e.target.checked ? 1 : 0
    )
  }

  const handleTextarea = (e: any) => {
    formik.setFieldValue(`${path}[${personIndex}].info_note`, e.target.value)
  }

  const handleAddress = (e: any, addressIndex: number) => {
    formik.setFieldValue(
      `${path}[${personIndex}].addresses[${addressIndex}].fullAddress`,
      e.target.value
    )
    // Manual editing invalidates the autocomplete selection — the user must re-select from the
    // list. Clearing the coords makes the address-complete-if-typed validation block the save.
    formik.setFieldValue(`${path}[${personIndex}].addresses[${addressIndex}].place_id`, '')
    formik.setFieldValue(`${path}[${personIndex}].addresses[${addressIndex}].latitude`, '')
    formik.setFieldValue(`${path}[${personIndex}].addresses[${addressIndex}].longitude`, '')
  }

  const handleCoordinates = (place: any, addressIndex: number) => {
    // Fallback selections (fetchFields failed) carry only place_id/formatted_address —
    // still record what we have so place_id-required validation can pass.
    const latitude = place.geometry?.location?.lat?.()
    const longitude = place.geometry?.location?.lng?.()
    // Shared extraction — matches a type ANYWHERE in a component's `types`, and falls back
    // past `locality` for the countries where Google does not return one. See
    // utils/placeComponents.ts for why the old types[0]/locality-only reads left City blank.
    const addressFields = extractAddressFields(place)
    const placeId = place.place_id
    // A SINGLE merged setFieldValue call — many un-awaited per-field calls each schedule
    // their own async Formik validation pass keyed to the values snapshot at that instant;
    // racing them can let a stale pass (still seeing place_id cleared) resolve last and
    // overwrite a fresh, valid result, leaving the "select an address" error stuck. One
    // atomic update means exactly one validation pass against the final merged data.
    formik.setFieldValue(`${path}[${personIndex}].addresses[${addressIndex}]`, {
      ...formik.values?.[path]?.[personIndex]?.addresses?.[addressIndex],
      ...addressFields,
      place_id: placeId,
      street_type: "",
      latitude,
      longitude,
      formatted_address: place.formatted_address || "",
      fullAddress: place.formatted_address || "",
      // A real Google pick always exits text-only mode (and manual, belt-and-suspenders).
      is_text_only: false,
      is_manual: false,
    })
  }

  const handleAddressNote = (e: any, addressIndex: number) => {
    formik.setFieldValue(
      `${path}[${personIndex}].addresses[${addressIndex}].info_note`,
      e.target.value
    )
  }

  // Two fixed phone slots per contact: Cell (Mobile) + Work. The house phone is a
  // household-level fact (not edited per contact here).
  const setPhoneSlotValue = (slotType: "Mobile" | "Work", number: string) => {
    const phones = formik?.values?.[path]?.[personIndex]?.phones || []
    formik.setFieldValue(`${path}[${personIndex}].phones`, setPhoneSlot(phones, slotType, number))
  }

  // The Work phone's dial extension (only meaningful alongside a work number).
  const setWorkExtensionValue = (value: string) => {
    const phones = formik?.values?.[path]?.[personIndex]?.phones || []
    formik.setFieldValue(`${path}[${personIndex}].phones`, setPhoneExtension(phones, value))
  }

  const handleEmail = (e: any) => {
    formik.setFieldValue(`${path}[${personIndex}].email`, e.target.value)
  }

  return (
    <>
      <div className="visit-report-update-contact__header">
        <PersonHeader
          formik={formik}
          personIndex={personIndex}
          path={path}
          setPrimaryPerson={handleIsPrimaryPerson}
          deletePerson={handleDeletePerson}
        />
      </div>
      <div className="visit-report-update-contact__personal-info">
        <PersonalInfo
          formik={formik}
          personIndex={personIndex}
          salutations={salutations}
          path={path}
          handleSalutation={handleSalutation}
          setGender={setGender}
          handleFirstName={handleFirstName}
          handleLastName={handleLastName}
          setIsJewish={setIsJewish}
          setIsChild={setIsChild}
          handleTextarea={handleTextarea}
        />
      </div>
      <div className="visit-report-update-contact__address-panel">
        <PersonPanel icon={marker} title={p('address')} />
      </div>
      {formik?.values?.[path]?.[personIndex].addresses.map(
        (_, addressIndex: number) => {
          return (
            <div
              key={addressIndex}
              className="visit-report-update-contact__address"
            >
              <Address
                formik={formik}
                personIndex={personIndex}
                addressIndex={addressIndex}
                path={path}
                handleAddress={handleAddress}
                handleCoordinates={handleCoordinates}
                handleAddressNote={handleAddressNote}
              />
            </div>
          )
        }
      )}

      {/* Only one optional Work address per contact — hide once one exists. */}
      {(formik?.values?.[path]?.[personIndex]?.addresses?.length ?? 0) === 0 && (
        <div className="visit-report-update-contact__add-button">
          <AddAddressButton title={p('add_address')} onClick={addAddress} />
        </div>
      )}
      <div>
        <ContactInfo
          formik={formik}
          personIndex={personIndex}
          path={path}
          setPhoneSlotValue={setPhoneSlotValue}
          setWorkExtensionValue={setWorkExtensionValue}
          handleEmail={handleEmail}
        />
      </div>
    </>
  )
}
